import React from 'react';
import { Box, Paper, Fade, Badge, Tooltip, Dialog, Typography, LinearProgress, Alert } from '@mui/material';
import { ChatOutlined as ChatOutlinedIcon } from '@mui/icons-material';
import { useChatbotState } from './hooks/useChatbotState';
import { ChatHeader } from './components/ChatHeader';
import { ChatMessages } from './components/ChatMessages';
import { ChatInput } from './components/ChatInput';
import { HistorySidebar } from './components/HistorySidebar';

interface ShipmentChatBotProps {
  orgCode?: string;
  botEnabled?: boolean;
  onNavigate?: (url: string) => void;
  apiBaseUrl?: string;
}

// ==================== MAIN ORCHESTRATOR ====================
const SpeedboxChatBot: React.FC<ShipmentChatBotProps> = ({
  orgCode = 'DEFAULT-ORG',
  botEnabled = true,
  onNavigate,
  apiBaseUrl,
}) => {
  const state = useChatbotState({ orgCode, onNavigate, apiBaseUrl });

  if (!botEnabled) {
    return null;
  }

  const hasMessages = state.messages.length > 0;

  const handleSuggestionClick = (query: string, intent?: string | null) => {
    state.setInputValue(query);
    setTimeout(() => state.handleSendMessage(query, intent || undefined), 100);
  };

  return (
    <>
      {/* INJECT ANIMATION KEYFRAMES */}
      <style>{`
        @keyframes geminiWave {
          0% { background-position: 0% 50%; }
          100% { background-position: 200% 50%; }
        }
        @keyframes geminiPulse {
          0% {
            box-shadow: 0 0 0 0 rgba(66, 133, 244, 0.4), 0 0 0 0 rgba(155, 114, 203, 0.3);
          }
          70% {
            box-shadow: 0 0 0 10px rgba(217, 101, 112, 0), 0 0 0 20px rgba(244, 180, 0, 0);
          }
          100% {
            box-shadow: 0 0 0 0 rgba(66, 133, 244, 0), 0 0 0 0 rgba(155, 114, 203, 0);
          }
        }
        @keyframes geminiBorderGlow {
          0% {
            border-color: #4285F4;
            box-shadow: 0 0 0 2px rgba(66, 133, 244, 0.4), 0 12px 40px rgba(66, 133, 244, 0.15);
          }
          25% {
            border-color: #9B72CB;
            box-shadow: 0 0 0 2px rgba(155, 114, 203, 0.4), 0 12px 40px rgba(155, 114, 203, 0.15);
          }
          50% {
            border-color: #D96570;
            box-shadow: 0 0 0 2px rgba(217, 101, 112, 0.4), 0 12px 40px rgba(217, 101, 112, 0.15);
          }
          75% {
            border-color: #F4B400;
            box-shadow: 0 0 0 2px rgba(244, 180, 0, 0.4), 0 12px 40px rgba(244, 180, 0, 0.15);
          }
          100% {
            border-color: rgba(0, 0, 0, 0.08);
            box-shadow: 0px 12px 40px rgba(0, 0, 0, 0.12);
          }
        }
      `}</style>

      {/* FLOATING LAUNCHER BOT BUTTON */}
      <Tooltip title={state.isOpen ? 'Close Chat' : 'Open Bot Assistant'}>
        <Badge
          badgeContent={!state.isOpen && hasMessages ? state.messages.length : 0}
          color="primary"
          sx={{
            position: 'fixed',
            bottom: 10, // Aligned to bottom: 100px
            right: 10,
            zIndex: 10000,
            '& .MuiBadge-badge': {
              backgroundColor: '#00C853',
              color: 'white',
            },
          }}
        >
          <Box
            onClick={() => state.setIsOpen(!state.isOpen)}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                state.setIsOpen(!state.isOpen);
              }
            }}
            role="button"
            tabIndex={0}
            aria-label={state.isOpen ? 'Close chatbot assistant' : 'Open chatbot assistant'}
            aria-haspopup="dialog"
            aria-expanded={state.isOpen}
            sx={{
              width: 40,
              height: 40,
              borderRadius: '16px',
              backgroundColor: '#111111',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 6px 20px rgba(0, 0, 0, 0.25)',
              color: 'white',
              cursor: 'pointer',
              transition: 'transform 0.2s ease, background-color 0.2s ease',
              '&:hover': {
                backgroundColor: '#222222',
                transform: 'scale(1.05)',
              },
              '&:active': {
                transform: 'scale(0.95)',
              },
              '&:focus-visible': {
                outline: '3px solid #00C853',
                outlineOffset: '2px',
              },
            }}
          >
            <ChatOutlinedIcon sx={{ fontSize: 20 }} />
          </Box>
        </Badge>
      </Tooltip>

      {/* CHAT WINDOW */}
      <Fade in={state.isOpen} unmountOnExit>
        <Paper
          ref={state.chatWindowRef}
          elevation={0}
          role="dialog"
          aria-label="ShipMate AI Chat Window"
          sx={{
            position: 'fixed',
            bottom: 60,
            right: 20,
            width: { xs: 'calc(100% - 40px)', sm: 400 },
            height: { xs: '65vh', sm: '640px' },
            maxHeight: '85vh',
            backgroundColor: '#FFFFFF',
            border: '1px solid rgba(0, 0, 0, 0.08)',
            boxShadow: '0px 12px 40px rgba(0, 0, 0, 0.12)',
            borderRadius: '24px',
            display: 'flex',
            flexDirection: 'column',
            zIndex: 10000,
            overflow: 'hidden',
            animation: state.isOpen
              ? `${
                  state.isActivating ? 'geminiBorderGlow 1.8s ease-in-out, ' : ''
                }geminiOpen 0.4s cubic-bezier(0.1, 0.8, 0.2, 1)`
              : 'none',
            '@keyframes geminiOpen': {
              '0%': {
                opacity: 0,
                transform: 'scale(0.85) translateY(24px)',
              },
              '100%': {
                opacity: 1,
                transform: 'scale(1) translateY(0)',
              },
            },
          }}
        >
          {/* HEADER */}
          <ChatHeader
            isActivating={state.isActivating}
            setIsOpen={state.setIsOpen}
            setShowSidebar={state.setShowSidebar}
            onClearChat={state.triggerClearChatConfirm}
            hasMessages={hasMessages}
          />

          {/* GEMINI ACTIVATION BAR */}
          <Box
            sx={{
              height: '3px',
              width: '100%',
              opacity: state.isActivating ? 1 : 0,
              transition: 'opacity 0.4s ease',
              background: 'linear-gradient(90deg, #4285F4, #9B72CB, #D96570, #F4B400, #4285F4)',
              backgroundSize: '200% 100%',
              animation: state.isActivating ? 'geminiWave 1.5s linear infinite' : 'none',
            }}
          />

          {/* ERROR ALERT */}
          {state.apiError && (
            <Alert severity="error" onClose={() => state.setApiError(null)} sx={{ margin: 1.5, borderRadius: 2 }}>
              {state.apiError}
            </Alert>
          )}

          {/* MESSAGES AREA */}
          <ChatMessages
            messages={state.messages}
            isLoading={state.isLoading}
            isActivating={state.isActivating}
            handleQuickAction={state.handleQuickAction}
            handleBotStateAction={state.handleBotStateAction}
            handleRelatedChipClick={state.handleRelatedChipClick}
            messagesEndRef={state.messagesEndRef}
            onEndConversation={state.triggerEndConversationConfirm}
            dismissConfirmation={state.dismissConfirmation}
            handleEndConversation={state.handleEndConversation}
            handleClearChat={state.handleClearChat}
            handleMissingFieldClick={state.handleMissingFieldClick}
          />

          {/* INPUT BAR AREA */}
          <ChatInput
            activeSuggestions={hasMessages ? state.activeSuggestions : []}
            attachedFiles={state.attachedFiles}
            inputValue={state.inputValue}
            setInputValue={state.setInputValue}
            isLoading={state.isLoading}
            handleSuggestionClick={handleSuggestionClick}
            handleSendMessage={state.handleSendMessage}
            handleFileSelect={state.handleFileSelect}
            removeFile={state.removeFile}
            fileInputRef={state.fileInputRef}
            inputRef={state.inputRef}
            activeIntent={state.activeIntent}
          />
        </Paper>
      </Fade>

      {/* SIDEBAR - CONVERSATION HISTORY */}
      <HistorySidebar
        showSidebar={state.showSidebar}
        setShowSidebar={state.setShowSidebar}
        conversationHistory={state.conversationHistory}
        getUserInitials={state.getUserInitials}
      />

      {/* FILE UPLOAD DIALOG */}
      <Dialog open={state.showFileDialog} onClose={() => state.setShowFileDialog(false)}>
        <Box sx={{ p: 2, minWidth: 300 }}>
          <Typography variant="subtitle2" sx={{ mb: 2 }}>
            Uploading file to S3...
          </Typography>
          <LinearProgress variant="determinate" value={state.uploadProgress} />
          <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
            {state.uploadProgress}% Complete
          </Typography>
        </Box>
      </Dialog>
    </>
  );
};

export default SpeedboxChatBot;
